import React from 'react';
import { Flex, DialogV2, ButtonV2, TextV2, Box } from '@withjoy/joykit';
import { ReactComponent as CheckLarge } from '@assets/icons/checklist-large.svg';
import { useCreditCardSetupContext } from '@apps/registry/admin/components/CreditCardSetup/CreditCardSetUp.context';
import { withWindow } from '@shared/utils/withWindow';
import { useTranslation } from '@shared/core';
import { useShouldShowConnectBankAccountButton } from '@apps/registry/common/utils/registryPayoutUtils';

export interface ReminderSentConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  guestName: string;
  paymentMethod?: string;
}

export const ReminderSentConfirmationDialog: React.FC<ReminderSentConfirmationDialogProps> = ({ isOpen, onClose, guestName, paymentMethod }) => {
  const { payoutAccountInfo, initKYCOnboarding, initPayoutAccountLoading } = useCreditCardSetupContext();
  const shouldShowBankAccountButton = useShouldShowConnectBankAccountButton();
  const { t } = useTranslation('yourRegistry');

  const { title, subtitle, paymentMethodInfo, whatMightHaveHappenedTitle, whatMightHaveHappenedBody, howToAvoidTitle, howToAvoidBody, connectBankAccountButton, cancelButton } =
    t('reminderSentConfirmationDialog');

  const handleConnectBankAccount = () => {
    if (!payoutAccountInfo) {
      initKYCOnboarding();
    } else {
      withWindow(window => {
        window.open(payoutAccountInfo.onboardingUrl || payoutAccountInfo.serviceDashboardUrl, '_self');
      });
    }
  };

  return (
    <DialogV2
      isOpen={isOpen}
      disableAutoFocus={true}
      disableCloseOnOutsideClick={true}
      disableCloseOnEscapeKeyClick={true}
      size="md"
    >
      <DialogV2.Header>
        <DialogV2.CloseButton onClick={onClose} />
      </DialogV2.Header>
      <DialogV2.Body>
        <Flex
          flexDirection="column"
          alignItems="center"
          width="100%"
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            marginBottom={3}
          >
            <Box
              height="48px"
              width="48px"
            >
              <CheckLarge />
            </Box>
          </Flex>
          <TextV2
            typographyVariant="hed4"
            color="mono14"
            textAlign="center"
            marginBottom={3}
          >
            {title({ guestName })}
          </TextV2>
          <TextV2
            typographyVariant="body2"
            color="mono12"
            textAlign="center"
          >
            {subtitle()}
          </TextV2>
        </Flex>

        <Flex
          flexDirection="column"
          marginTop={6}
        >
          <TextV2
            typographyVariant="body1"
            color="mono12"
            marginBottom={6}
          >
            {paymentMethodInfo({ paymentMethod })}
          </TextV2>

          <TextV2
            typographyVariant="hed2"
            color="mono14"
            marginBottom={3}
          >
            {whatMightHaveHappenedTitle()}
          </TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono12"
            marginBottom={6}
          >
            {whatMightHaveHappenedBody()}
          </TextV2>

          <TextV2
            typographyVariant="hed2"
            color="mono14"
            marginBottom={3}
          >
            {howToAvoidTitle()}
          </TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono12"
            marginBottom={5}
          >
            {howToAvoidBody()}
          </TextV2>

          {shouldShowBankAccountButton && (
            <ButtonV2
              intent="newPrimary"
              shape="rounded"
              fullWidth
              onClick={handleConnectBankAccount}
              loading={initPayoutAccountLoading}
            >
              {connectBankAccountButton()}
            </ButtonV2>
          )}
          <ButtonV2
            intent="neutral"
            variant="ghost"
            onClick={onClose}
            textAlign="center"
            width="100%"
          >
            {cancelButton()}
          </ButtonV2>
        </Flex>
      </DialogV2.Body>
    </DialogV2>
  );
};
